import { useEffect, useState } from 'react';

import { useToken } from '../auth/use-token.js';

import { createMcpClient, type McpClientHandle } from './mcp-client.js';

/**
 * Lazily constructs one MCP client per mounted consumer. The client is
 * closed on unmount. Components needing tools should gate on `ready`.
 */
export function useMcp(): { handle: McpClientHandle | null; ready: boolean; error: Error | null } {
  const getToken = useToken();
  const [handle, setHandle] = useState<McpClientHandle | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    let local: McpClientHandle | null = null;
    void createMcpClient(getToken)
      .then((h) => {
        if (cancelled) {
          void h.close();
          return;
        }
        local = h;
        setHandle(h);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      });
    return () => {
      cancelled = true;
      if (local) void local.close();
    };
  }, [getToken]);

  return { handle, ready: handle !== null, error };
}
